import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, Phone, Mail, Pencil, Truck, User } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';

const Row = ({ label, value, testid }) => (
  <div className="flex justify-between gap-4 py-2.5 border-b border-slate-100 dark:border-slate-800 text-sm">
    <span className="text-slate-600 dark:text-slate-300">{label}</span>
    <span className="font-medium text-slate-900 dark:text-white text-right break-all" data-testid={testid}>{value || '—'}</span>
  </div>
);

/** Quick driver card: contact actions (WhatsApp / call / email) + jump to edit. */
export const DriverInfoDialog = ({ driver, summary, onOpenChange }) => {
  const navigate = useNavigate();
  if (!driver) return null;
  const digits = (driver.phone || '').replace(/\D/g, '');
  const wa = digits ? `https://wa.me/${digits.length === 10 ? `1${digits}` : digits}` : null;
  return (
    <Dialog open={!!driver} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="driver-info-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <span className="h-10 w-10 rounded-xl bg-blue-600 text-white flex items-center justify-center"><User className="h-5 w-5" /></span>
            <span>
              <span className="block text-lg">{driver.name}</span>
              <span className="block text-sm font-normal text-slate-600 dark:text-slate-300 capitalize">{(driver.status || 'active').replace('_', ' ')}</span>
            </span>
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-2">
          <Button asChild className={`w-full bg-[#25D366] hover:bg-[#1ebe5b] text-white ${wa ? '' : 'pointer-events-none opacity-40'}`} data-testid="driver-info-whatsapp">
            <a href={wa || undefined} target="_blank" rel="noreferrer"><MessageCircle className="h-4 w-4 mr-1.5" />WhatsApp</a>
          </Button>
          <Button asChild variant="outline" className={`w-full ${digits ? '' : 'pointer-events-none opacity-40'}`} data-testid="driver-info-call">
            <a href={digits ? `tel:${driver.phone}` : undefined}><Phone className="h-4 w-4 mr-1.5" />Call</a>
          </Button>
          <Button asChild variant="outline" className={`w-full ${driver.email ? '' : 'pointer-events-none opacity-40'}`} data-testid="driver-info-email">
            <a href={driver.email ? `mailto:${driver.email}` : undefined}><Mail className="h-4 w-4 mr-1.5" />Email</a>
          </Button>
        </div>
        <div>
          <Row label="Phone" value={driver.phone} testid="driver-info-phone" />
          <Row label="Email" value={driver.email} testid="driver-info-email-value" />
          <Row label="Unit" value={driver.assigned_unit_number ? <span className="inline-flex items-center gap-1"><Truck className="h-3.5 w-3.5" />Unit {driver.assigned_unit_number}</span> : null} />
          <Row label="Pay" value={summary?.pay_basis} />
          <Row label="Block pay" value={driver.block_pay ? `$${Number(driver.block_pay).toFixed(2)} / block` : null} />
          <Row label="License" value={[driver.license_number, driver.license_state].filter(Boolean).join(' · ')} />
          <Row label="License expires" value={driver.license_expiry} />
        </div>
        <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white" onClick={() => navigate(`/drivers?edit=${driver.id}`)} data-testid="driver-info-edit">
          <Pencil className="h-4 w-4 mr-1.5" />Edit driver
        </Button>
      </DialogContent>
    </Dialog>
  );
};
